import { formatDaysString, formatNumberLocale } from "@/utils/helpers";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from "@/components/ui/dialog";
import { InvestmentTransactionModals } from "@/constants/enums";
import { FramerButton, ProgressBar } from "@/components/shared";
import { ServiceProviderDetails } from "@/components/shared";
import { useTranslation } from "react-i18next";
import { updateInvestmentState } from "@/cache/appstate/WriteQueries";
import { useBlockchainData } from "@/hooks";
import { useIntl } from "react-intl";
import { ChangeEvent, useState } from "react";
import { useWeb3 } from "@/context/Web3Context";
import { propsMakeInvestmentModal } from "../props";
import { Input } from "@/components/ui/input";
import { handleValidation } from "@/utils/form";

function MakeInvestmentModal(props: propsMakeInvestmentModal) {

  // Props
  const { assetFinancingPool, state: _state, setShowModal } = props;

  // States
  const [inputErrorText, setInputErrorText] = useState("");
  const [init, setInit] = useState(true);

  // Hooks
  const intl = useIntl();
  const { t } = useTranslation(["modals"]);
  const blockchainData = useBlockchainData();

  // Magic SDK
  const {
    connectWallet,
    isConnected,
  } = useWeb3();

  // Determine if modal is open
  const isOpen = true; // This modal is only rendered when it should be shown

  /**
   * Handle modal close
   */
  const handleClose = () => {
    setShowModal(InvestmentTransactionModals.NoModal);
    updateInvestmentState({ amount: 0 });
  };

  /**
   * OnChange handler for investment amount input field
   */
  function handleOnChange(event: ChangeEvent<HTMLInputElement>) {
    setInit(false);

    const { name, value } = event.target;
    const isValid = handleValidation(value, "float");
    let errorText = "";

    const parsedValue = parseFloat(value);
    const isMultipleOf500 = parsedValue % 500 === 0;

    if (!isValid) {
      errorText = t("makeinvestment.input.error.invalid");
    } else if (parsedValue <= 0) {
      errorText = t("makeinvestment.input.error.zero");
    } else if (parsedValue > blockchainData.balance) {
      errorText = t("makeinvestment.input.error.insufficient");
    } else if (!isMultipleOf500) {
      errorText = t("makeinvestment.input.error.invalidstep");
    } else if (parsedValue > assetFinancingPool!.financinglimit - assetFinancingPool!.amountinvested) {
      errorText = t("makeinvestment.input.error.exceedsfinancinglimit");
    }

    setInputErrorText(errorText);

    if ((isValid || value.length === 0) && isMultipleOf500) {
      const finalValue = value.length === 0 ? 0 : parsedValue;
      updateInvestmentState({ [name]: finalValue });
    }
  }

  /**
   * OnClick handle for MAX button
   */
  const handleMaxClick = () => {
    const input = document.getElementById("investmentAmount") as HTMLInputElement;
    if (input) {
      const balance = blockchainData.balance;
      const remainingInvestmentCapacity = assetFinancingPool!.financinglimit - assetFinancingPool!.amountinvested;
      const inputValue = Math.min(Math.floor(balance / 500) * 500, remainingInvestmentCapacity);

      input.value = `${inputValue}`;
      updateInvestmentState({ amount: inputValue });

      // Create proper event structure and trigger validation
      const syntheticEvent = {
        target: {
          name: "amount",
          value: `${inputValue}`,
        }
      } as ChangeEvent<HTMLInputElement>;

      handleOnChange(syntheticEvent);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {if(!open){ handleClose();}}} modal>
      {/*
        We pull out the overlay so we can give it its own z-index.
        By default, shadcn's DialogOverlay sits on top of everything.
        Here we push it to z-20 so that the Magic modal (which usually
        injects at z-50+) can sit above it.
      */}
      <DialogOverlay className="fixed inset-0 bg-black/50 z-20 pointer-events-none" />
      <DialogContent
      // Prevent any "pointer down" outside the content area (backdrop click)
        // from closing the Dialog. Radix will honor event.preventDefault().
        onPointerDownOutside={(event) => {
          event.preventDefault();
        }}
        className="px-6 min-w-3xl max-w-5xl max-h-[90vh] overflow-y-auto dark:bg-mfdarklight"
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold dark:text-white">
            {t("makeinvestment.title")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* SP Details */}
          <div>
            <ServiceProviderDetails
              serviceProviderId={assetFinancingPool!.spdetails._id}
            />
          </div>

          {/* Funds locked */}
          <div>
            <p className="text-sm dark:text-white">
              {t("makeinvestment.text")}
              <span className="font-semibold">
                {` ${formatDaysString(assetFinancingPool!.dayslocked)}`}
              </span>
            </p>
          </div>

          <div className="space-y-2">
            {/* Financing Limit */}
            <div className="flex items-center">
              <p className="text-sm font-semibold w-[200px] text-gray-800 dark:text-white">
                {t("makeinvestment.financinglimit")}
              </p>
              <p className="font-semibold text-base text-mfpurple dark:text-mforange">
                {formatNumberLocale(intl, assetFinancingPool!.financinglimit, "currency")}
              </p>
            </div>
            {/* Total Invested */}
            <div className="flex items-center">
              <p className="text-sm font-semibold w-[200px] text-gray-800 dark:text-white">
                {t("makeinvestment.totalinvested")}
              </p>
              <p className="font-semibold text-base text-mfpurple dark:text-mforange">
                {formatNumberLocale(intl, assetFinancingPool!.amountinvested, "currency")}
              </p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="md:mr-[100px]">
            <ProgressBar
              label={t("components:serviceproviderinfo.totalinvested")}
              percentage={Math.round(assetFinancingPool!.amountinvested/assetFinancingPool!.financinglimit*100)}
            />
          </div>

          {
            isConnected ?
              <>
                {/* Available balance */}
                <div className="flex items-center">
                  <p className="text-sm font-semibold w-[200px] text-gray-800 dark:text-white">
                    {t("makeinvestment.availablebalance")}:
                  </p>
                  <p className="text-mforange font-semibold">
                    {formatNumberLocale(intl, blockchainData.balance, "currency")}
                  </p>
                </div>
                {/* Input box label */}
                <p className="text-lg font-bold text-gray-800 dark:text-white">
                  {t("makeinvestment.input.label")}
                </p>
                {/* Input field and buttons */}
                <div className="flex flex-col sm:flex-row flex-wrap w-full gap-y-4">
                  <div className="flex-1 bg-gray-100 dark:bg-mfdarklighter rounded-md p-2 w-full sm:mr-2">
                    {/* Currency symbol */}
                    <div className="flex items-center">
                      <p className="font-semibold text-4xl text-gray-500 dark:text-white pl-2">
                        €
                      </p>
                      {/* Input box */}
                      <div className="w-full">
                        <Input
                          id="investmentAmount"
                          onChange={(event: ChangeEvent<HTMLInputElement>) => handleOnChange(event)}
                          type="float"
                          name="amount"
                          placeholder={"0.00"}
                          className="ml-1 h-[36px] text-3xl bg-transparent dark:bg-transparent md:text-3xl lg:text-4xl placeholder:text-3xl md:placeholder:text-3xl lg:placeholder:text-4xl font-bold dark:placeholder:text-gray-100 dark:text-gray-100 border-none ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                        />
                      </div>
                      {/* Max button */}
                      <div>
                        <FramerButton
                          onClick={handleMaxClick}
                          className="text-xs font-bold px-[10px] py-[4px] bg-white dark:bg-mfdarklight
                            dark:text-white text-gray-800 rounded-md hover:text-gray-300 dark:hover:bg-mfdarklight/80"
                        >
                          {t("makeinvestment.input.max")}
                        </FramerButton>
                      </div>
                    </div>
                  </div>
                  {/* Invest button */}
                  <div className="">
                    <FramerButton
                      disabled={init || inputErrorText.length > 0}
                      onClick={() => {
                        setShowModal(InvestmentTransactionModals.InvestmentTransactionSummary);
                      }}
                      className={`h-full uppercase text-white ${init || inputErrorText.length > 0 ? "bg-gray-300 cursor-not-allowed dark:bg-gray-800" : "bg-mforange hover:bg-mforange/80"} w-full`}
                    >
                      {t("makeinvestment.input.button")}
                    </FramerButton>
                  </div>
                </div>
                <p className="h-4 text-xs text-red-600 mt-1">{inputErrorText}</p>
              </>:
              <div className="flex items-center space-x-2">
                <p className="text-sm font-semibold dark:text-white">
                  {t("common:text.investconnectwallet")}.
                </p>
                <FramerButton
                  className="bg-mforange text-white hover:bg-mforange/80"
                  onClick={connectWallet}
                >
                  {t("common:text.connectwallet")}
                </FramerButton>
              </div>
          }
        </div>

        <DialogFooter>
          <div className="flex w-full justify-start">
            {/* Cancel button */}
            <FramerButton
              onClick={handleClose}
              variant="outline-primary"
            >
              {t("common:buttons.cancel")}
            </FramerButton>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default MakeInvestmentModal;
